// Grabadora de notas de voz (diseño §12.4, S6). webm/opus donde se pueda; mp4 en Safari. Máximo 3 min
// (aviso a los 2:30): el diseño decía 10, pero la transcripción corre en el panel dentro de una petición
// HTTP y más de ~3 min no cabe en su tiempo (revisión L-a). Una nota NUNCA se pierde: al parar (a mano o
// por llegar al máximo) queda en `nota` hasta que se envía bien o se descarta (revisión V1).
// Sin micrófono (permiso denegado o sin dispositivo) → `error = 'sin_permiso'`.
import { useCallback, useEffect, useRef, useState } from 'react';

export const MAX_MS = 3 * 60_000;
export const AVISO_MS = 150_000;

export type EstadoGrabadora = 'quieta' | 'grabando';
export interface NotaGrabada {
  audio: Blob;
  mime: string;
  duracionMs: number;
}

function mimePreferido(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const m of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']) {
    if (MediaRecorder.isTypeSupported?.(m)) return m;
  }
  return '';
}

// Notas listas por agente mientras dura la pestaña (revisión L-2): cambiar de chat no las pierde, y al
// volver al chat de ese agente siguen ahí. Nunca se envían a otro agente.
const notasListas = new Map<string, NotaGrabada>();

export function useGrabadora(agentId: string) {
  const [estado, setEstado] = useState<EstadoGrabadora>('quieta');
  const [ms, setMs] = useState(0);
  const [error, setError] = useState<'sin_permiso' | 'no_soportado' | null>(null);
  const [nota, setNota] = useState<NotaGrabada | null>(() => notasListas.get(agentId) ?? null);
  const rec = useRef<MediaRecorder | null>(null);
  const trozos = useRef<Blob[]>([]);
  const inicio = useRef(0);
  const reloj = useRef<ReturnType<typeof setInterval> | null>(null);
  const alTerminar = useRef<((n: NotaGrabada | null) => void) | null>(null);

  const limpiar = useCallback(() => {
    if (reloj.current) clearInterval(reloj.current);
    reloj.current = null;
    rec.current?.stream.getTracks().forEach((t) => t.stop());
    rec.current = null;
    setEstado('quieta');
    setMs(0);
  }, []);

  // Al desmontar (p. ej. cambiar de agente con la grabación en marcha): se para y la nota queda guardada
  // para ESE agente (onstop la guarda); no se entrega a nadie.
  useEffect(
    () => () => {
      alTerminar.current = null;
      if (rec.current && rec.current.state !== 'inactive') rec.current.stop();
      else limpiar();
    },
    [limpiar],
  );

  const empezar = useCallback(async () => {
    setError(null);
    const mime = mimePreferido();
    if (!mime || !navigator.mediaDevices?.getUserMedia) return setError('no_soportado');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      return setError('sin_permiso');
    }
    const r = new MediaRecorder(stream, { mimeType: mime });
    trozos.current = [];
    r.ondataavailable = (e) => e.data.size && trozos.current.push(e.data);
    r.onstop = () => {
      const grabada: NotaGrabada = { audio: new Blob(trozos.current, { type: mime }), mime, duracionMs: Date.now() - inicio.current };
      const fin = alTerminar.current;
      alTerminar.current = null;
      limpiar();
      // Siempre queda guardada (también si paró por llegar al máximo, sin nadie esperando, o porque Pau
      // cambió de chat a mitad de grabación).
      if (grabada.audio.size > 0) {
        notasListas.set(agentId, grabada);
        setNota(grabada);
      }
      fin?.(grabada);
    };
    rec.current = r;
    inicio.current = Date.now();
    r.start(1000);
    setEstado('grabando');
    reloj.current = setInterval(() => {
      const t = Date.now() - inicio.current;
      setMs(t);
      if (t >= MAX_MS) r.stop();
    }, 250);
  }, [limpiar, agentId]);

  /** Para y devuelve la nota (o null si se canceló o no había nada). */
  const parar = useCallback(
    () =>
      new Promise<NotaGrabada | null>((resolver) => {
        if (!rec.current || rec.current.state === 'inactive') return resolver(null);
        alTerminar.current = resolver;
        rec.current.stop();
      }),
    [],
  );

  const cancelar = useCallback(() => {
    if (!rec.current) return;
    alTerminar.current = null;
    rec.current.onstop = null;
    try {
      rec.current.stop();
    } catch {
      // ya estaba parada
    }
    limpiar();
  }, [limpiar]);

  /** Olvida la nota lista (después de enviarla bien, o porque Pau la descarta). */
  const olvidar = useCallback(() => {
    notasListas.delete(agentId);
    setNota(null);
  }, [agentId]);

  return { estado, ms, error, nota, empezar, parar, cancelar, olvidar };
}

export const mmss = (ms: number) => `${Math.floor(ms / 60_000)}:${String(Math.floor((ms % 60_000) / 1000)).padStart(2, '0')}`;
